import PusherService from "./PusherService";
import XanoAPI from "./XanoAPI";

interface ClientLock {
  clientId: string;
  userId: string;
  userName: string;
  lockedAt: string;
  expiresAt: string;
}

interface LockEvent {
  clientId: string;
  userId: string;
  userName: string;
  action: "locked" | "unlocked" | "expired";
  timestamp: string;
}

class ClientLockService {
  private static instance: ClientLockService;
  private pusherService: PusherService;
  private api: XanoAPI;
  private activeLocks: Map<string, ClientLock> = new Map();
  private isInitialized: boolean = false;
  private readonly STORAGE_KEY = "rpm_client_locks";
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly LOCK_DURATION = 30 * 60 * 1000; // 30 minutes
  private readonly HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private cachedCurrentUser: any = null; // Cache to avoid repeated API calls
  private beforeUnloadHandler: (() => void) | null = null;
  private changeListeners: Set<() => void> = new Set(); // Listeners for lock state changes

  private constructor() {
    this.pusherService = PusherService.getInstance();
    this.api = XanoAPI.getInstance();
  }

  public static getInstance(): ClientLockService {
    if (!ClientLockService.instance) {
      console.log("🏗️ Creating NEW ClientLockService instance");
      ClientLockService.instance = new ClientLockService();
    } else {
      console.log("♻️ Reusing EXISTING ClientLockService instance");
    }
    return ClientLockService.instance;
  }

  /**
   * Initialize the client lock service
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // Initialize Pusher service (non-blocking)
      this.pusherService.initialize();

      // Get current user - no timeout, let it complete naturally
      try {
        this.cachedCurrentUser = await this.api.getCurrentUser();
      } catch (error) {
        console.error("Failed to get current user for lock service:", error);
        // Don't block initialization - just skip user-specific setup
        this.isInitialized = true;
        return;
      }

      if (!this.cachedCurrentUser?.agency_id) {
        console.warn(
          "User not part of an agency, skipping lock service initialization"
        );
        this.isInitialized = true;
        return;
      }

      // Restore locks from localStorage
      this.restoreLocksFromStorage();

      // Subscribe to Pusher channel for real-time updates (PRIVATE channel with auth)
      try {
        // Using private channel with authentication
        const channelName = `private-client-locks-${this.cachedCurrentUser.agency_id}`;
        const channel = this.pusherService.subscribe(channelName);

        // Bind to server-triggered events (NOT client-triggered)
        channel.bind("client-locked", async (data: LockEvent) => {
          console.log("📥 Raw Pusher event data (client-locked):", data);
          await this.handleLockEvent(data);
        });

        channel.bind("client-unlocked", (data: LockEvent) => {
          console.log("========================================");
          console.log("📥 RAW PUSHER EVENT: client-unlocked");
          console.log("📥 Raw Pusher event data (client-unlocked):", data);
          console.log("📥 Event clientId:", data.clientId);
          console.log("📥 Event userId:", data.userId);
          console.log("📥 Event userName:", data.userName);
          console.log("========================================");
          this.handleUnlockEvent(data);
        });

        channel.bind("client-expired", (data: LockEvent) => {
          console.log("📥 Raw Pusher event data (client-expired):", data);
          this.handleExpiredEvent(data);
        });

        // Debug: Listen to ALL events on this channel
        channel.bind_global((eventName: string, eventData: any) => {
          console.log(
            `📨 [DEBUG] Pusher event received: ${eventName}`,
            eventData
          );
        });

        console.log(
          `✓ Subscribed to PRIVATE channel: ${channelName} (authenticated)`
        );
      } catch (error) {
        console.error("Failed to subscribe to Pusher channel:", error);
        console.log("Falling back to localStorage with 5-second polling");
      }

      // Start heartbeat to maintain active locks
      try {
        this.startHeartbeat();
      } catch (error) {
        console.error("Failed to start heartbeat:", error);
        // Continue initialization even if heartbeat fails
      }

      // Add beforeunload handler for reliable unlock on page close/refresh
      this.beforeUnloadHandler = () => {
        console.log("🔓 Page unloading - releasing all locks");
        // Get all locks owned by current user
        const myLocks = Array.from(this.activeLocks.entries()).filter(
          ([_, lock]) => lock.userId === this.cachedCurrentUser?.id.toString()
        );

        // Release each lock synchronously
        myLocks.forEach(([clientId, _]) => {
          this.unlockClientSync(clientId);
        });
      };
      window.addEventListener("beforeunload", this.beforeUnloadHandler);

      this.isInitialized = true;
      console.log("Client lock service initialized successfully");
    } catch (error) {
      console.error("Failed to initialize client lock service:", error);
      // Don't throw error to avoid blocking navigation
      this.isInitialized = true;
    }
  }

  /**
   * Attempt to lock a client for editing
   */
  public async lockClient(clientId: string): Promise<boolean> {
    try {
      // Use cached user if available, otherwise fetch
      const currentUser =
        this.cachedCurrentUser || (await this.api.getCurrentUser());
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      // Update cache if we had to fetch
      if (!this.cachedCurrentUser) {
        this.cachedCurrentUser = currentUser;
      }

      // Check if client is already locked by someone else
      const existingLock = this.activeLocks.get(clientId);
      if (existingLock && existingLock.userId !== currentUser.id.toString()) {
        console.log(
          `❌ Client ${clientId} is locked by user ${existingLock.userId}`
        );
        return false; // Client is locked by another user
      }

      // Create lock object
      const lock: ClientLock = {
        clientId,
        userId: currentUser.id.toString(),
        userName: currentUser.name || `User ${currentUser.id}`,
        lockedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + this.LOCK_DURATION).toISOString(),
      };

      // Store lock locally
      this.activeLocks.set(clientId, lock);
      console.log(
        `🔒 Lock acquired for client ${clientId} by user ${currentUser.id}`
      );

      // Save to localStorage
      this.saveLocksToStorage();

      // Notify listeners
      this.notifyListeners();

      // Broadcast lock event
      await this.broadcastLockEvent({
        clientId,
        userId: currentUser.id.toString(),
        userName: lock.userName,
        action: "locked",
        timestamp: lock.lockedAt,
      });

      console.log(`📡 Lock broadcast sent for client ${clientId}`);
      return true;
    } catch (error) {
      console.error("Error locking client:", error);
      return false;
    }
  }

  /**
   * Release a client lock (async - waits for broadcast to complete)
   * Use this for React Router navigation where we have time to complete
   */
  public async unlockClient(clientId: string): Promise<void> {
    try {
      console.log(`🔓 ASYNC unlock starting for client ${clientId}...`);

      // Use cached user if available, otherwise fetch
      const currentUser =
        this.cachedCurrentUser || (await this.api.getCurrentUser());
      if (!currentUser) {
        console.warn(`⚠️ No current user for unlock`);
        return;
      }

      // Update cache if we had to fetch
      if (!this.cachedCurrentUser) {
        this.cachedCurrentUser = currentUser;
      }

      const lock = this.activeLocks.get(clientId);
      if (!lock) {
        console.warn(`⚠️ No lock found for client ${clientId}`);
        return;
      }

      if (lock.userId !== currentUser.id.toString()) {
        console.warn(
          `⚠️ Cannot unlock client ${clientId}: not owned by current user (${currentUser.id})`
        );
        return;
      }

      // Remove lock immediately
      this.activeLocks.delete(clientId);
      console.log(`🔓 Lock removed from memory for client ${clientId}`);

      // Save to localStorage
      this.saveLocksToStorage();
      console.log(`💾 Lock removed from localStorage`);

      // Notify listeners BEFORE broadcast (for instant local UI update)
      this.notifyListeners();

      // Broadcast unlock event (with timeout to not block navigation)
      console.log(`📡 Broadcasting unlock event for client ${clientId}...`);
      try {
        // Use Promise.race to timeout after 2 seconds (prevents blocking navigation)
        await Promise.race([
          this.broadcastLockEvent({
            clientId,
            userId: currentUser.id.toString(),
            userName: lock.userName,
            action: "unlocked",
            timestamp: new Date().toISOString(),
          }),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error("Broadcast timeout")), 2000)
          ),
        ]);
        console.log(`✅ ASYNC unlock completed for client ${clientId}`);
      } catch (broadcastError) {
        // Don't throw - unlock already happened locally
        console.warn(
          `⚠️ Broadcast warning for client ${clientId}:`,
          broadcastError instanceof Error
            ? broadcastError.message
            : broadcastError
        );
        console.log(`✅ Local unlock completed (broadcast attempted)`);
      }
    } catch (error) {
      console.error(`❌ Error unlocking client ${clientId}:`, error);
    }
  }

  /**
   * Synchronous unlock for page unload (fire-and-forget)
   * Uses fetch with keepalive for reliable delivery during page close/refresh
   * Use this for beforeunload events where we can't wait for async completion
   */
  public unlockClientSync(clientId: string): void {
    try {
      const lock = this.activeLocks.get(clientId);
      if (!lock) {
        console.log(`⚠️ No lock found for client ${clientId}`);
        return;
      }

      console.log(`🔓 SYNC unlock for client ${clientId}`);
      console.log(
        `🔍 Active locks BEFORE delete:`,
        Array.from(this.activeLocks.keys())
      );
      console.log(
        `🔍 localStorage BEFORE delete:`,
        localStorage.getItem(this.STORAGE_KEY)
      );

      // Remove from memory and localStorage immediately
      this.activeLocks.delete(clientId);
      this.saveLocksToStorage();

      console.log(
        `🔍 Active locks AFTER delete:`,
        Array.from(this.activeLocks.keys())
      );
      console.log(
        `🔍 Lock for ${clientId} after delete:`,
        this.activeLocks.get(clientId)
      );
      console.log(
        `🔍 localStorage AFTER delete:`,
        localStorage.getItem(this.STORAGE_KEY)
      );

      // Notify listeners for instant local UI update
      console.log(`🔔 About to notify ${this.changeListeners.size} listeners`);
      this.notifyListeners();
      console.log(`✅ Listeners notified`);

      // Broadcast unlock event to other users via Pusher
      const eventData: LockEvent = {
        clientId,
        userId: lock.userId,
        userName: lock.userName,
        action: "unlocked",
        timestamp: new Date().toISOString(),
      };

      console.log(`📡 Broadcasting unlock event for client ${clientId}...`);
      this.broadcastLockEvent(eventData)
        .then(() => {
          console.log(`✅ Unlock broadcast succeeded for client ${clientId}`);
        })
        .catch((err) => {
          console.error(`⚠️ Unlock broadcast failed (non-critical):`, err);
          // Don't block unlock - local state is already updated
        });
    } catch (error) {
      // Don't let unlock errors break navigation
      console.error("❌ Error in sync unlock (non-critical):", error);
    }
  }

  /**
   * Check if a client is locked
   */
  public isClientLocked(clientId: string): boolean {
    const lock = this.activeLocks.get(clientId);
    if (!lock) return false;

    // Check if lock has expired
    if (new Date() > new Date(lock.expiresAt)) {
      this.activeLocks.delete(clientId);
      return false;
    }

    return true;
  }

  /**
   * Get lock information for a client
   */
  public getClientLock(clientId: string): ClientLock | null {
    const lock = this.activeLocks.get(clientId);
    if (!lock) return null;

    // Check if lock has expired
    if (new Date() > new Date(lock.expiresAt)) {
      this.activeLocks.delete(clientId);
      return null;
    }

    return lock;
  }

  /**
   * Check if current user has locked a client
   * Uses cached user to avoid repeated API calls
   */
  public async isClientLockedByCurrentUser(clientId: string): Promise<boolean> {
    // Use cached user if available to avoid repeated API calls
    const currentUser =
      this.cachedCurrentUser ||
      (await this.api.getCurrentUser().catch(() => null));
    if (!currentUser) return false;

    const lock = this.activeLocks.get(clientId);
    if (!lock) return false;

    return (
      lock.userId === currentUser.id.toString() && !this.isLockExpired(lock)
    );
  }

  /**
   * Release all locks held by current user
   */
  public async releaseAllLocks(): Promise<void> {
    // Use cached user if available, otherwise fetch
    const currentUser =
      this.cachedCurrentUser || (await this.api.getCurrentUser());
    if (!currentUser) return;

    const locksToRelease = Array.from(this.activeLocks.entries())
      .filter(([, lock]) => lock.userId === currentUser.id.toString())
      .map(([clientId]) => clientId);

    for (const clientId of locksToRelease) {
      await this.unlockClient(clientId);
    }

    // Clear from localStorage
    this.clearLocksFromStorage();
  }

  /**
   * Start heartbeat to maintain active locks
   */
  private startHeartbeat(): void {
    this.heartbeatInterval = setInterval(async () => {
      const currentUser = await this.api.getCurrentUser();
      if (!currentUser) return;

      // Extend locks for current user
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [_clientId, lock] of this.activeLocks.entries()) {
        if (lock.userId === currentUser.id.toString()) {
          lock.expiresAt = new Date(
            Date.now() + this.LOCK_DURATION
          ).toISOString();
        }
      }

      // Save updated locks to localStorage
      this.saveLocksToStorage();
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Stop heartbeat
   */
  private stopHeartbeat(): void {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Broadcast lock event to other users via Pusher (server-side).
   * Uses XanoAPI.broadcastClientLockEvent() which calls the messages/new endpoint
   * to trigger Pusher events on the agency-specific channel.
   *
   * @param {LockEvent} event - The lock event to broadcast
   * @returns {Promise<void>}
   *
   * @example
   * ```typescript
   * await this.broadcastLockEvent({
   *   clientId: '123',
   *   userId: '456',
   *   userName: 'John Doe',
   *   action: 'locked',
   *   timestamp: Date.now()
   * });
   * ```
   */
  private async broadcastLockEvent(event: LockEvent): Promise<void> {
    try {
      const currentUser = await this.api.getCurrentUser();
      if (!currentUser?.agency_id) {
        console.warn("Cannot broadcast - no agency_id");
        return;
      }

      // Build event data as a JSON object
      const eventData = {
        clientId: event.clientId,
        userId: event.userId,
        userName: event.userName,
        action: event.action,
        timestamp: event.timestamp,
      };

      console.log(
        `📡 Attempting to broadcast ${event.action} for client ${event.clientId}...`
      );
      console.log("📦 Payload being sent:", {
        name: `client-${event.action}`,
        message: eventData,
      });

      // Use XanoAPI method to broadcast the event (HTTP, not WebSocket)
      const response = await this.api.broadcastClientLockEvent({
        name: `client-${event.action}`,
        message: JSON.stringify(eventData),
      });

      console.log(
        `✅ Successfully broadcasted ${event.action} for client ${event.clientId} via Xano/Pusher`
      );
      console.log("📥 Xano response:", response);
    } catch (error) {
      // Log but don't throw - lock state already updated locally
      console.warn(
        "⚠️ Broadcast warning (lock state already updated locally):",
        error instanceof Error ? error.message : error
      );
      throw error; // Re-throw so caller knows broadcast failed
    }
  }

  /**
   * Handle incoming lock events
   */
  private async handleLockEvent(data: LockEvent): Promise<void> {
    const currentUser = await this.api.getCurrentUser();

    // Check if this is our own lock event
    const isOwnEvent = currentUser && data.userId === currentUser.id.toString();

    console.log(
      `📥 Pusher: Lock event for client ${data.clientId} by ${data.userName}${isOwnEvent ? " (own event)" : ""}`
    );

    // Always process lock events (including our own) to ensure UI updates
    const lock: ClientLock = {
      clientId: data.clientId,
      userId: data.userId,
      userName: data.userName,
      lockedAt: data.timestamp,
      expiresAt: new Date(Date.now() + this.LOCK_DURATION).toISOString(),
    };

    this.activeLocks.set(data.clientId, lock);
    this.saveLocksToStorage();

    // Notify listeners for real-time UI update (critical for table refresh)
    console.log(
      `🔔 Notifying ${this.changeListeners.size} listeners for lock update`
    );
    this.notifyListeners();
    console.log(`✅ Lock processed and listeners notified`);
  }

  /**
   * Handle incoming unlock events
   */
  private handleUnlockEvent(data: LockEvent): void {
    console.log(`📥 Pusher: Received unlock event for client ${data.clientId}`);
    console.log(`📥 Event data:`, data);
    console.log(`📥 Current user will be checked...`);
    console.log(`📥 Active locks BEFORE:`, Array.from(this.activeLocks.keys()));

    // Check if this lock exists
    const existingLock = this.activeLocks.get(data.clientId);
    if (!existingLock) {
      console.log(
        `⚠️ No lock found for client ${data.clientId} - already unlocked?`
      );
    } else {
      console.log(`📥 Existing lock found:`, existingLock);
      console.log(`📥 Lock being removed by userId: ${data.userId}`);
    }

    // Remove the lock regardless of who initiated it
    this.activeLocks.delete(data.clientId);
    this.saveLocksToStorage();

    console.log(`📥 Active locks AFTER:`, Array.from(this.activeLocks.keys()));
    console.log(
      `📥 Lock for ${data.clientId} after delete:`,
      this.activeLocks.get(data.clientId)
    );
    console.log(
      `📥 Pusher: Client ${data.clientId} unlocked - lock removed from memory`
    );

    // Notify listeners for real-time UI update
    console.log(
      `🔔 About to notify ${this.changeListeners.size} listeners for unlock`
    );
    this.notifyListeners();
    console.log(`✅ Unlock listeners notified for client ${data.clientId}`);
  }

  /**
   * Handle expired lock events
   */
  private handleExpiredEvent(data: LockEvent): void {
    console.log(
      `📥 Pusher: Received expired event for client ${data.clientId}`
    );
    this.activeLocks.delete(data.clientId);
    this.saveLocksToStorage();

    // Notify listeners for real-time UI update
    this.notifyListeners();
  }

  /**
   * Check if a lock has expired
   */
  private isLockExpired(lock: ClientLock): boolean {
    return new Date() > new Date(lock.expiresAt);
  }

  /**
   * Check if the service is initialized
   */
  public isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  /**
   * Subscribe to lock state changes
   * Returns an unsubscribe function
   */
  public onLockStateChange(callback: () => void): () => void {
    this.changeListeners.add(callback);
    return () => {
      this.changeListeners.delete(callback);
    };
  }

  /**
   * Notify all listeners that lock state has changed
   */
  private notifyListeners(): void {
    console.log(
      `🔔 Notifying ${this.changeListeners.size} listeners of lock state change`
    );
    console.log(`🔔 Current locks:`, Array.from(this.activeLocks.keys()));
    let callbackCount = 0;
    this.changeListeners.forEach((callback) => {
      try {
        callbackCount++;
        console.log(
          `🔔 Calling listener ${callbackCount}/${this.changeListeners.size}`
        );
        callback();
        console.log(`✅ Listener ${callbackCount} completed`);
      } catch (error) {
        console.error(
          `❌ Error in lock state change listener ${callbackCount}:`,
          error
        );
      }
    });
    console.log(`✅ All ${this.changeListeners.size} listeners notified`);
  }

  /**
   * Save locks to localStorage
   */
  private saveLocksToStorage(): void {
    try {
      const locksArray = Array.from(this.activeLocks.entries());
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(locksArray));
    } catch (error) {
      console.error("Failed to save locks to storage:", error);
    }
  }

  /**
   * Restore locks from localStorage
   */
  private restoreLocksFromStorage(): void {
    try {
      const storedLocks = localStorage.getItem(this.STORAGE_KEY);
      console.log(`💾 Restoring locks from localStorage:`, storedLocks);
      if (storedLocks) {
        const locksArray = JSON.parse(storedLocks);
        this.activeLocks = new Map(locksArray);

        // Clean up expired locks
        const now = new Date();
        let expiredCount = 0;
        for (const [clientId, lock] of this.activeLocks.entries()) {
          if (new Date(lock.expiresAt) < now) {
            console.log(`💾 Removing expired lock for client ${clientId}`);
            this.activeLocks.delete(clientId);
            expiredCount++;
          }
        }

        if (expiredCount > 0) {
          console.log(`💾 Removed ${expiredCount} expired locks`);
          this.saveLocksToStorage();
        }

        console.log(
          `💾 Restored ${this.activeLocks.size} valid locks:`,
          Array.from(this.activeLocks.keys())
        );
      }
    } catch (error) {
      console.error("Failed to restore locks from storage:", error);
      this.activeLocks = new Map();
    }
  }

  /**
   * Clear locks from localStorage
   */
  private clearLocksFromStorage(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear locks from storage:", error);
    }
  }

  /**
   * Cleanup method
   */
  public destroy(): void {
    this.stopHeartbeat();
    this.releaseAllLocks();

    // Remove beforeunload handler
    if (this.beforeUnloadHandler) {
      window.removeEventListener("beforeunload", this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }

    this.isInitialized = false;
    this.cachedCurrentUser = null;
  }
}

export default ClientLockService;
